import React from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Theme,
} from "@mui/material";
import AppTableHeaderCell from "../../components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../components/common/pages/AppTablePagenation";
import { SortType } from "../../../models/SortType";
import { WalletType } from "../../../models/Swagstore/SwagstoreWalletListing";
import { WalletTransactionApiRow } from "../../../models/Swagstore/SwagstoreWalletTransactions";
import { formatWalletBalance, formatWalletDate } from "./walletFormatters";
import {
  formatTransactionPerformedBy,
  formatTransactionSource,
  isCreditTransaction,
} from "./walletTransactionFormatters";
import {
  getModernPageColors,
  modernEmptyStateSx,
  modernTableBodyCellSx,
  modernTableHeaderCellSx,
  modernTablePaperSx,
  modernTableRowSx,
} from "../../../styles/modernListingPageSx";

interface SwagstoreWalletTransactionsTableProps {
  rows: WalletTransactionApiRow[];
  loading: boolean;
  pageNo: number;
  limit: number;
  totalCount: number;
  sortType: SortType;
  walletType: WalletType;
  onSortChange: (sortType: SortType) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
}

const CREATED_DATE_COLUMN = "created_date";

const SwagstoreWalletTransactionsTable: React.FC<
  SwagstoreWalletTransactionsTableProps
> = ({
  rows,
  loading,
  pageNo,
  limit,
  totalCount,
  sortType,
  walletType,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const isCustomerWallet = walletType === WalletType.CUSTOMER;
  const isEndUserWallet = walletType === WalletType.END_USER;

  return (
    <Paper
      sx={{
        ...modernTablePaperSx(theme),
        mt: 0,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {loading ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ ...modernEmptyStateSx(theme), minHeight: 200 }}>
          <Typography variant="body1">No transactions found.</Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <TableContainer sx={{ flex: 1, overflow: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    minWidth="120px"
                    sortColumn={CREATED_DATE_COLUMN}
                    sortBy={CREATED_DATE_COLUMN}
                    sortDirection={sortType}
                    onSortChange={(_column, newSortType) =>
                      onSortChange(newSortType)
                    }
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Date
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="100px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Type
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="120px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Source
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="100px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Amount
                  </AppTableHeaderCell>
                  {isCustomerWallet ? (
                    <AppTableHeaderCell
                      minWidth="160px"
                      fontSize="0.75rem"
                      sx={modernTableHeaderCellSx(theme)}
                    >
                      Sent To
                    </AppTableHeaderCell>
                  ) : null}
                  <AppTableHeaderCell
                    minWidth="120px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Balance After
                  </AppTableHeaderCell>
                  {isEndUserWallet ? (
                    <AppTableHeaderCell
                      minWidth="140px"
                      fontSize="0.75rem"
                      sx={modernTableHeaderCellSx(theme)}
                    >
                      Shipment ID
                    </AppTableHeaderCell>
                  ) : null}
                  {isCustomerWallet ? (
                    <AppTableHeaderCell
                      minWidth="140px"
                      fontSize="0.75rem"
                      sx={modernTableHeaderCellSx(theme)}
                    >
                      Payment Order ID
                    </AppTableHeaderCell>
                  ) : null}
                  <AppTableHeaderCell
                    minWidth="160px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Performed By
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const isCredit = isCreditTransaction(row.transaction_type);
                  return (
                    <TableRow key={`wallet-txn-${row.id}`} hover sx={modernTableRowSx(theme)}>
                      <TableCell sx={modernTableBodyCellSx(theme)}>
                        {formatWalletDate(row.created_date)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...modernTableBodyCellSx(theme),
                          textTransform: "capitalize",
                        }}
                      >
                        {row.transaction_type}
                      </TableCell>
                      <TableCell sx={modernTableBodyCellSx(theme)}>
                        {formatTransactionSource(row.source)}
                      </TableCell>
                      <TableCell sx={modernTableBodyCellSx(theme)}>
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 700,
                            color: isCredit ? colors.credit : colors.debit,
                          }}
                        >
                          {isCredit ? "+" : "-"}
                          {formatWalletBalance(row.amount)}
                        </Box>
                      </TableCell>
                      {isCustomerWallet ? (
                        <TableCell sx={modernTableBodyCellSx(theme)}>
                          {row.sent_to || "—"}
                        </TableCell>
                      ) : null}
                      <TableCell sx={modernTableBodyCellSx(theme)}>
                        {formatWalletBalance(row.balance_after)}
                      </TableCell>
                      {isEndUserWallet ? (
                        <TableCell sx={modernTableBodyCellSx(theme)}>
                          {row.shipment_id?.toString() || "—"}
                        </TableCell>
                      ) : null}
                      {isCustomerWallet ? (
                        <TableCell sx={modernTableBodyCellSx(theme)}>
                          {row.partner_order_id || "—"}
                        </TableCell>
                      ) : null}
                      <TableCell sx={modernTableBodyCellSx(theme)}>
                        {formatTransactionPerformedBy(row)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={pageNo}
            limit={limit}
            totalCount={totalCount}
            onPageChange={onPageChange}
            handleRowsChange={onRowsPerPageChange}
          />
        </Box>
      )}
    </Paper>
  );
};

export default React.memo(SwagstoreWalletTransactionsTable);
